/**
 * Sesión web/PWA:
 * - Flag + user en localStorage (sobreviven cierre de PWA)
 * - JWT en localStorage + memoria: necesario cuando API y SPA son cross-origin
 *   (cookie HttpOnly con SameSite=Lax no viaja en XHR cross-site)
 * - La cookie HttpOnly del backend sigue siendo complementaria same-site
 */
import Cookies from 'js-cookie'

const SESSION_FLAG = 'auth_session'
const USER_KEY = 'user'
const TOKEN_KEY = 'auth_token'
const LEGACY_TOKEN_KEY = 'token'

let memoryToken = null

function safeStorageGet(storage, key) {
  try {
    return storage.getItem(key)
  } catch {
    return null
  }
}

function safeStorageSet(storage, key, value) {
  try {
    storage.setItem(key, value)
  } catch {
    // ignore (Safari private mode, quota, etc.)
  }
}

function safeStorageRemove(storage, key) {
  try {
    storage.removeItem(key)
  } catch {
    // ignore
  }
}

export function clearLegacyTokenStores() {
  safeStorageRemove(localStorage, TOKEN_KEY)
  safeStorageRemove(localStorage, LEGACY_TOKEN_KEY)
  try {
    Cookies.remove('auth_token')
  } catch {
    // ignore
  }
}

export function markSessionActive(active = true) {
  if (active) {
    safeStorageSet(localStorage, SESSION_FLAG, '1')
    safeStorageSet(sessionStorage, SESSION_FLAG, '1')
  } else {
    safeStorageRemove(localStorage, SESSION_FLAG)
    safeStorageRemove(sessionStorage, SESSION_FLAG)
  }
}

export function isSessionFlagActive() {
  return (
    safeStorageGet(localStorage, SESSION_FLAG) === '1' ||
    safeStorageGet(sessionStorage, SESSION_FLAG) === '1'
  )
}

export function setMemoryToken(token) {
  memoryToken = token || null
}

export function getMemoryToken() {
  return memoryToken
}

/** Token persistido para restaurar sesión al reabrir la PWA */
export function getPersistedToken() {
  return (
    safeStorageGet(localStorage, TOKEN_KEY) ||
    safeStorageGet(localStorage, LEGACY_TOKEN_KEY) ||
    null
  )
}

/** @deprecated usar getPersistedToken */
export function getLegacyReadableToken() {
  return getPersistedToken()
}

export function persistAccessToken(token) {
  if (!token) {
    safeStorageRemove(localStorage, TOKEN_KEY)
    safeStorageRemove(localStorage, LEGACY_TOKEN_KEY)
    setMemoryToken(null)
    return
  }
  setMemoryToken(token)
  safeStorageSet(localStorage, TOKEN_KEY, token)
  // Evitar duplicar en clave legacy
  safeStorageRemove(localStorage, LEGACY_TOKEN_KEY)
}

export function persistUser(user) {
  if (!user) {
    safeStorageRemove(sessionStorage, USER_KEY)
    safeStorageRemove(localStorage, USER_KEY)
    return
  }
  const raw = JSON.stringify(user)
  safeStorageSet(sessionStorage, USER_KEY, raw)
  safeStorageSet(localStorage, USER_KEY, raw)
}

export function readPersistedUser() {
  try {
    const raw =
      safeStorageGet(sessionStorage, USER_KEY) ||
      safeStorageGet(localStorage, USER_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function clearClientSession() {
  memoryToken = null
  markSessionActive(false)
  persistUser(null)
  clearLegacyTokenStores()
}

export function beginClientSession({ token, user } = {}) {
  // No usar clearLegacyTokenStores aquí: borraría el token que acabamos de guardar.
  try {
    Cookies.remove('auth_token')
  } catch {
    // ignore
  }

  if (token) {
    persistAccessToken(token)
  } else {
    // Login solo-cookie: hidratar memoria desde storage si ya había token
    setMemoryToken(getPersistedToken())
  }

  markSessionActive(true)
  if (user) persistUser(user)
}

/** ¿Hay indicios de sesión para intentar restaurar vía /auth/me? */
export function hasSessionHint() {
  return (
    isSessionFlagActive() ||
    !!readPersistedUser() ||
    !!getMemoryToken() ||
    !!getPersistedToken()
  )
}

// Rehidratar Bearer en memoria al cargar el bundle (reopen PWA / refresh)
try {
  memoryToken =
    safeStorageGet(localStorage, TOKEN_KEY) ||
    safeStorageGet(localStorage, LEGACY_TOKEN_KEY) ||
    null
} catch {
  memoryToken = null
}
